/**
 * EPIC-97 (radial wheel, client wave 2026-09-25): the quick-action wheel's
 * touch-only "More actions" button (`quick-action-more`) is removed — see
 * PMA-COCK-012's own contract entry for that dated reversal record. This
 * panel itself is still mounted by `ActionStage.tsx` at `currentStep ===
 * "selectAction"`: a keyboard jersey-number selection with no field anchor
 * still reaches that step (`useActionFlow.ts`'s `handlePlayerClick`), and it
 * has no other action controls to fall back on (judge round-1 point 8 — an
 * earlier pass in this wave deleted the render branch along with the
 * button, restored in the same wave).
 */
import { TFunction } from "i18next";
import { Player } from "../../types";
import { formatDisplayName } from "../../../../lib/nameFormat";

interface ActionSelectionPanelProps {
  actions: string[];
  selectedPlayer: Player | null;
  // PMA-COCK-147: renamed from `isSubmitting` -- an ack-latency "syncing"
  // state must never disable these buttons (that silently swallows a
  // legitimate tap); only a genuine lock (cockpitLocked) may.
  disabled: boolean;
  keyHints: Record<string, string>;
  onActionSelect: (action: string) => void;
  onCancel: () => void;
  t: TFunction<"logger">;
  /** Top-3 predicted next actions to visually highlight. */
  suggestedActions?: string[];
}

const getShortcutForAction = (
  action: string,
  keyHints: Record<string, string>,
) => {
  return Object.keys(keyHints).find(
    (key) => keyHints[key] === action && key === key.toUpperCase(),
  );
};

const ActionSelectionPanel = ({
  actions,
  selectedPlayer,
  disabled,
  keyHints,
  onActionSelect,
  onCancel,
  t,
  suggestedActions = [],
}: ActionSelectionPanelProps) => (
  <div
    className="bg-white rounded-lg shadow p-6"
    data-testid="action-selection"
  >
    <h2 className="text-lg font-semibold mb-4">
      {t("selectAction")} - {formatDisplayName(selectedPlayer?.full_name)} #
      {selectedPlayer?.jersey_number}
    </h2>
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {actions.map((action) => {
        const isSuggested = suggestedActions.includes(action);
        return (
          <button
            key={action}
            onClick={() => onActionSelect(action)}
            disabled={disabled}
            data-testid={`action-btn-${action}`}
            className={`py-4 rounded-lg font-medium transition-colors ${
              disabled
                ? "bg-blue-300 text-white cursor-not-allowed"
                : isSuggested
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white ring-2 ring-emerald-400/50"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
            }`}
          >
            {t(`action${action}`)}
            <span className="block text-xs opacity-60 font-mono mt-1">
              [{getShortcutForAction(action, keyHints) ?? " "}]
            </span>
          </button>
        );
      })}
      <button
        onClick={onCancel}
        className="py-4 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-medium transition-colors"
      >
        {t("cancel")}
      </button>
    </div>
  </div>
);

export default ActionSelectionPanel;
